import type { Task, TaskPriority, TaskViewPreferences } from './types';

/** Priority numeric values for sorting (higher = more urgent) */
const PRIORITY_ORDER: Record<TaskPriority, number> = {
  high: 3,
  medium: 2,
  low: 1,
  none: 0,
};

/** Sort tasks according to view preferences */
export function sortTasks(tasks: Task[], prefs: TaskViewPreferences): Task[] {
  const sorted = [...tasks];
  const dir = prefs.sort_direction === 'desc' ? -1 : 1;

  sorted.sort((a, b) => {
    let cmp = 0;
    switch (prefs.sort_by) {
      case 'priority':
        cmp = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
        // For priority, default "asc" means low→high numerically,
        // but users expect "high first" as the natural priority sort,
        // so we invert: desc = high first (default feel), asc = low first
        break;
      case 'created_at':
        cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        break;
      case 'title':
        cmp = a.title.localeCompare(b.title);
        break;
      case 'position':
      default:
        cmp = a.position - b.position;
        break;
    }
    return cmp * dir;
  });

  return sorted;
}

/** Group labels and their tasks, in display order */
export interface TaskGroup {
  key: string;
  label: string;
  icon: string;
  tasks: Task[];
}

const PRIORITY_GROUPS: { key: TaskPriority; label: string; icon: string }[] = [
  { key: 'high', label: 'High', icon: '🔴' },
  { key: 'medium', label: 'Medium', icon: '🟠' },
  { key: 'low', label: 'Low', icon: '🔵' },
  { key: 'none', label: 'No Priority', icon: '—' },
];

/** Group tasks by the configured group_by preference. Returns sorted groups. */
export function groupTasks(tasks: Task[], prefs: TaskViewPreferences): TaskGroup[] {
  if (prefs.group_by === 'none') {
    return [{ key: 'all', label: '', icon: '', tasks: sortTasks(tasks, prefs) }];
  }

  // Group by priority
  const byPriority = new Map<TaskPriority, Task[]>();
  for (const t of tasks) {
    const list = byPriority.get(t.priority) || [];
    list.push(t);
    byPriority.set(t.priority, list);
  }

  // Within each group, sort by the configured sort (or position as fallback)
  const withinGroupPrefs: TaskViewPreferences = {
    ...prefs,
    // When grouped by priority, sorting by priority within group is meaningless — use position
    sort_by: prefs.sort_by === 'priority' ? 'position' : prefs.sort_by,
    sort_direction: prefs.sort_by === 'priority' ? 'asc' : prefs.sort_direction,
  };

  return PRIORITY_GROUPS.filter(
    (g) => byPriority.has(g.key) && byPriority.get(g.key)!.length > 0,
  ).map((g) => ({
    key: g.key,
    label: g.label,
    icon: g.icon,
    tasks: sortTasks(byPriority.get(g.key)!, withinGroupPrefs),
  }));
}

/** Whether manual drag reorder is allowed with current preferences */
export function isDragReorderAllowed(prefs: TaskViewPreferences): boolean {
  return prefs.sort_by === 'position' && prefs.group_by === 'none';
}

/** Human-readable labels */
export const SORT_BY_LABELS: Record<string, string> = {
  position: 'Manual (drag order)',
  priority: 'Priority',
  created_at: 'Date created',
  title: 'Title (A-Z)',
};

export const SORT_DIRECTION_LABELS: Record<string, string> = {
  asc: 'Ascending',
  desc: 'Descending',
};

export const GROUP_BY_LABELS: Record<string, string> = {
  none: 'None',
  priority: 'Priority',
};
